import React, { useState } from "react";
import { Box, styled } from "@mui/material";
import { Icon } from "../Icon";
import { OverflowTooltip } from "../OverflowTooltip";
import { IssueBoxContainerProps } from "./types";
import { useIsShown } from "../../../shared/hooks/useIsShown";

const StyledContainer = styled(Box)({
  cursor: "pointer",
  width: "100%",
  position: "relative",
  overflow: "hidden",
});

const StyledTrigger = styled(Box, {
  shouldForwardProp: (prop) => prop !== "isOpen",
})<{ isOpen: boolean }>(({ isOpen }) => ({
  minHeight: "39px",
  display: "flex",
  alignItems: "center",
  position: "relative",
  backgroundColor: isOpen ? "#FFFFFF" : "rgba(0, 0, 0, 0.04)",
  borderTop: "1px solid rgba(0, 0, 0, 0.12)",
  "&:hover": {
    backgroundColor: "rgba(0, 0, 0, 0.08)",
  },
  "&:active": {
    backgroundColor: "rgba(0, 0, 0, 0.08)",
  },
}));

const StyledContentContainer = styled(Box)({
  width: "100%",
  height: "100%",
  display: "flex",
  alignItems: "center",
  padding: "0 16px",
  gap: "8px",
});

const StyledIconContainer = styled(Box)({
  width: "16px",
  height: "16px",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
});

const StyledColorIconContainer = styled(Box)<{ color?: string }>(
  ({ color }) => ({
    width: "12px",
    height: "12px",
    borderRadius: "4px",
    backgroundColor: color || "#1D74FF",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  })
);

const StyledTriggerTextContainer = styled(Box)({
  maxWidth: "180px",
  display: "flex",
  flexDirection: "column",
  gap: "2px",
});

const StyledPanel = styled(Box)({
  minHeight: "52px",
  display: "flex",
  alignItems: "flex-start",
  position: "relative",
  opacity: 1,
  overflow: "hidden",
  backgroundColor: "#FFFFFF",
});

export const IssueBoxContainer: React.FC<IssueBoxContainerProps> = ({
  color,
  title,
  subTitle,
  open: controlledOpen,
  onToggle,
  children,
  rightSlot,
  className,
}) => {
  const [isShown, open, close, toggle] = useIsShown(false);
  const isOpen = controlledOpen !== undefined ? controlledOpen : isShown;

  const handleToggle = () => {
    const newOpen = !isOpen;

    if (controlledOpen === undefined) {
      if (newOpen) {
        open();
      } else {
        close();
      }
    }

    if (onToggle) {
      onToggle(newOpen);
    }
  };

  const childrenArray = React.Children.toArray(children);

  return (
    <StyledContainer className={className}>
      <StyledTrigger onClick={handleToggle} isOpen={isOpen}>
        <StyledContentContainer>
          <StyledIconContainer>
            <Icon
              name={isOpen ? "16/arrow/top" : "16/arrow/bottom"}
              size={16}
            />
          </StyledIconContainer>

          <StyledColorIconContainer color={color}>
            <div
              style={{
                width: "8px",
                height: "8px",
                backgroundColor: color || "#1D74FF",
                borderRadius: "2px",
              }}
            />
          </StyledColorIconContainer>

          <StyledTriggerTextContainer>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "flex-start",
                width: "100%",
                flex: 1,
                gap: "4px",
              }}
            >
              <OverflowTooltip
                text={title}
                variant="subtitle2"
                sx={{
                  fontWeight: "500",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              />
              <OverflowTooltip
                text={subTitle}
                sx={{
                  fontWeight: 400,
                  fontSize: "12px",
                  color: "rgba(0, 0, 0, 0.6)",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              />
            </Box>
          </StyledTriggerTextContainer>
        </StyledContentContainer>
        {rightSlot && (
          <Box
            onClick={(e) => e.stopPropagation()}
            sx={{
              position: "absolute",
              right: "8px",
              top: "50%",
              transform: "translateY(-50%)",
              display: "flex",
              alignItems: "center",
              zIndex: 1,
            }}
          >
            {rightSlot}
          </Box>
        )}
      </StyledTrigger>

      {isOpen && childrenArray.length > 0 && (
        <StyledPanel>
          <Box sx={{ display: "flex", flexDirection: "column", width: "100%" }}>
            {childrenArray}
          </Box>
        </StyledPanel>
      )}
    </StyledContainer>
  );
};
